import { useCallback } from 'react';

import { useAuth } from '@/app/providers/auth/authContext';
import { uidSchema, type Team } from '@/core/schemas';
import { activeTournamentId } from '@/features/tournament/useTournament';
import { subscribeMyTeam } from '@/infrastructure/repositories/teamRepository';
import { useLiveData, type LiveData } from '@/shared/hooks/useLiveData';

/**
 * The signed-in captain's team, or `null` once it is established that none is
 * registered yet. `undefined` still means "unknown" — the distinction decides
 * whether the registration form or a spinner is shown.
 */
export function useMyTeam(): LiveData<Team | null> {
  const { session } = useAuth();
  const uid = session.status === 'authenticated' ? session.account.uid : null;

  const subscribe = useCallback(
    (onData: (value: Team | null) => void, onError: Parameters<typeof subscribeMyTeam>[3]) => {
      if (uid === null) {
        onData(null);
        return () => undefined;
      }
      return subscribeMyTeam(activeTournamentId, uidSchema.parse(uid), onData, onError);
    },
    [uid],
  );

  return useLiveData<Team | null>(['team', activeTournamentId, uid ?? 'anonymous'], subscribe);
}
